import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {formatDate} from '../../../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {
  InlineBanner,
  InlineBannerTypesEnum,
} from '../../../../../../libs/shared/ui/src/components/banners/inlineBanner/inlineBanner';
import {MILLISECONDS_PER_DAY} from '../../../../../settings/billing/helpers';
import {useComputeConversationCutoffDays} from '../../conversation/conversationSummary/conversationSummaryRetention';
import {BasicViewerBase} from './basicViewerBase';

/*
 * Style.
 */

const StyledStaticDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
`;

/*
 * Props.
 */

interface BasicViewDataRetentionBannerProps {
  lastCommentOrReplyDate: number | undefined;
  inboxes: ReadonlyArray<InboxModel>;
}

/*
 * Component.
 */

export const BasicViewDataRetentionBanner: FC<BasicViewDataRetentionBannerProps> = (props) => {
  const {inboxes, lastCommentOrReplyDate} = props;
  const localize = useLocalize();

  const cutoffDays = useComputeConversationCutoffDays(inboxes);
  if (!cutoffDays || !lastCommentOrReplyDate) {
    return null;
  }

  const trashDate = lastCommentOrReplyDate + cutoffDays * MILLISECONDS_PER_DAY;

  // Handle when deletion is delayed by a day due to large number of conversations.
  const trashDateStr =
    trashDate < Date.now() ? (
      ''
    ) : (
      <Trans id="activity-basic-trashed-retention-date">
        on {formatDate(localize, DateTime.fromMillis(trashDate), DateTime.DATE_FULL)}{' '}
      </Trans>
    );

  return (
    <StyledStaticDiv>
      <BasicViewerBase isPreviousActivityBasic={false}>
        <InlineBanner
          type={InlineBannerTypesEnum.INFO}
          title={
            <strong>
              <Trans id="activity-basic-trashed-retention-banner-1">
                This conversation will be permanently deleted{' '}
              </Trans>
              {trashDateStr}
              <Trans id="activity-basic-trashed-retention-banner-2">
                in accordance with your data retention policy
              </Trans>
            </strong>
          }
        />
      </BasicViewerBase>
    </StyledStaticDiv>
  );
};
